import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValueEvent, useReducedMotion, useScroll, useTransform } from "motion/react";
import { ArrowRight, ArrowUpRight, Clock } from "@phosphor-icons/react";
import { guides, problemCategories } from "../../data/content.js";
import { ClipPhoto, EASE_OUT, MaskWords, PageFrame, Seo, useMediaQuery } from "../../kit.jsx";
import { GUIDE_ART, minutes } from "./learnData.js";
import "./learn.css";

const TOTAL_MINUTES = guides.reduce((sum, guide) => sum + minutes(guide.readTime), 0);

// Builds a winding road through each stop marker. On a narrow screen the markers
// share one x position, so the road becomes a straight line down the left edge.
function roadPath(points, wide) {
  if (!points.length) return "";
  let d = `M ${points[0].x} ${points[0].y}`;
  for (let i = 1; i < points.length; i += 1) {
    const a = points[i - 1];
    const b = points[i];
    const dy = b.y - a.y;
    const bow = wide ? (i % 2 ? 96 : -96) : 0;
    d += ` C ${a.x + bow} ${a.y + dy * 0.45} ${b.x + bow} ${b.y - dy * 0.45} ${b.x} ${b.y}`;
  }
  return d;
}

function RouteStage() {
  const stageRef = useRef(null);
  const reduceMotion = useReducedMotion();
  const wide = useMediaQuery("(min-width: 900px)");
  const [geometry, setGeometry] = useState({ d: "", width: 0, height: 0, points: [] });
  const aheadRef = useRef(null);
  const marksRef = useRef([]);
  const [reached, setReached] = useState(reduceMotion ? guides.length + 1 : 0);
  const { scrollYProgress } = useScroll({ target: stageRef, offset: ["start 0.55", "end 0.75"] });
  const pathLength = useTransform(scrollYProgress, (value) => Math.min(1, Math.max(0, value)));

  useLayoutEffect(() => {
    const stage = stageRef.current;
    if (!stage) return undefined;
    const measure = () => {
      const box = stage.getBoundingClientRect();
      const points = [...stage.querySelectorAll("[data-lb-marker]")].map((node) => {
        const rect = node.getBoundingClientRect();
        return { x: rect.left - box.left + rect.width / 2, y: rect.top - box.top + rect.height / 2 };
      });
      const height = stage.offsetHeight;
      setGeometry({ d: roadPath(points, wide), width: stage.offsetWidth, height, points });
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(stage);
    return () => observer.disconnect();
  }, [wide]);

  // Each marker lights up when the drawn line reaches it, measured along the path itself.
  useEffect(() => {
    const path = aheadRef.current;
    if (!path || !geometry.points.length) return;
    const total = path.getTotalLength();
    marksRef.current = geometry.points.map((point) => {
      let low = 0;
      let high = total;
      for (let step = 0; step < 24; step += 1) {
        const mid = (low + high) / 2;
        if (path.getPointAtLength(mid).y < point.y - 0.5) low = mid;
        else high = mid;
      }
      return total ? high / total : 0;
    });
  }, [geometry]);

  useMotionValueEvent(scrollYProgress, "change", (value) => {
    if (reduceMotion) return;
    const count = marksRef.current.filter((mark) => value >= mark - 0.005).length;
    setReached((previous) => (previous === count ? previous : count));
  });

  useEffect(() => {
    if (reduceMotion) setReached(guides.length + 1);
  }, [reduceMotion]);

  return (
    <section className="lb-route" aria-labelledby="lb-route-title">
      <h2 id="lb-route-title" className="sr-only">The five guides, in order</h2>
      <div className="lb-route__stage" ref={stageRef}>
        {geometry.d && (
          <svg
            className="lb-route__road"
            width={geometry.width}
            height={geometry.height}
            viewBox={`0 0 ${geometry.width} ${geometry.height}`}
            aria-hidden="true"
          >
            <path ref={aheadRef} className="lb-route__ahead" d={geometry.d} />
            <motion.path className="lb-route__done" d={geometry.d} style={reduceMotion ? undefined : { pathLength }} />
          </svg>
        )}
        <ol className="lb-route__stops">
          {guides.map((guide, index) => {
            const art = GUIDE_ART[guide.slug];
            const done = index < reached;
            return (
              <li key={guide.slug} id={`stop-${guide.slug}`} className={`lb-stop ${index % 2 ? "lb-stop--flip" : ""}`}>
                <span className={`lb-stop__marker ${done ? "is-reached" : ""}`} data-lb-marker aria-hidden="true">
                  {String(index + 1).padStart(2, "0")}
                </span>
                <div className="lb-stop__copy">
                  <span className="lb-mono">Stop {index + 1} of {guides.length} · {art.stop}</span>
                  <h3>
                    <Link to={`/guides/${guide.slug}`}>{guide.title}</Link>
                  </h3>
                  <p className="lb-stop__summary">{guide.summary}</p>
                  <p className="lb-stop__outcome"><span>You leave with</span>{guide.outcome.replace(/^Leave with /, "")}</p>
                  <dl className="lb-stop__meta">
                    <div><dt>Reading time</dt><dd><Clock weight="bold" aria-hidden="true" />{guide.readTime}</dd></div>
                    <div><dt>Moves</dt><dd>{guide.sections.length} steps and a checklist</dd></div>
                  </dl>
                  <Link className="gh-link" to={`/guides/${guide.slug}`}>
                    Read the guide <ArrowRight weight="bold" aria-hidden="true" />
                  </Link>
                </div>
                <ClipPhoto className="lb-stop__photo" src={art.photo} alt={art.alt} sizes="(max-width: 900px) 100vw, 40vw" />
              </li>
            );
          })}
        </ol>
        <div className="lb-stop lb-stop--finish">
          <span className={`lb-stop__marker lb-stop__marker--end ${reached > guides.length ? "is-reached" : ""}`} data-lb-marker aria-hidden="true" />
          <p className="lb-stop__copy">
            <span className="lb-mono">End of the route</span>
            <strong>You have a problem, a brief, a first version and a way to explain it.</strong>
          </p>
        </div>
      </div>
    </section>
  );
}

function RoutePreview() {
  const reduceMotion = useReducedMotion();
  return (
    <motion.nav
      className="lb-preview"
      aria-label="Jump to a guide"
      initial={reduceMotion ? false : { opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.9, delay: 0.55, ease: EASE_OUT }}
    >
      <ol>
        {guides.map((guide, index) => (
          <li key={guide.slug}>
            <a href={`#stop-${guide.slug}`}>
              <span className="lb-preview__dot" aria-hidden="true">{index + 1}</span>
              <strong>{GUIDE_ART[guide.slug].stop}</strong>
              <small>{guide.readTime}</small>
            </a>
          </li>
        ))}
      </ol>
    </motion.nav>
  );
}

export default function GuidesHub() {
  const reduceMotion = useReducedMotion();
  const first = guides[0];
  const appear = (delay) => ({
    initial: reduceMotion ? false : { opacity: 0, y: 18 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.9, delay, ease: EASE_OUT },
  });
  return (
    <PageFrame className="rd-page learn-page lb-guides">
      <Seo
        title="Guides for Young Builders"
        path="/guides"
        description="Practical Project GRID guides for finding problems, building prototypes and presenting student projects."
      />
      <main>
        <header className="lb-ghub-hero">
          <motion.p className="lb-mono lb-ghub-hero__meta" {...appear(0)}>
            Project guides · {guides.length} guides · {TOTAL_MINUTES} minutes of reading
          </motion.p>
          <MaskWords as="h1" text="A field manual for your first project." onMount delay={0.05} />
          <div className="lb-ghub-hero__row">
            <motion.p {...appear(0.4)}>
              Five short guides, in the order you will need them. Start with a problem you have noticed and finish with a pitch you can defend. Each one ends with a checklist you can tick off.
            </motion.p>
            <motion.div className="gh-actions" {...appear(0.5)}>
              <Link className="gh-btn gh-btn--fill" to={`/guides/${first.slug}`}>
                <span className="roll" data-label="Start with stop 1"><span>Start with stop 1</span></span>
              </Link>
              <Link className="gh-btn gh-btn--ghost" to="/problems">
                <span className="roll" data-label="Browse problem areas"><span>Browse problem areas</span></span>
              </Link>
            </motion.div>
          </div>
          <RoutePreview />
        </header>

        <RouteStage />

        <section className="lb-ghub-next" aria-labelledby="lb-ghub-next-title">
          <div className="lb-ghub-next__copy">
            <MaskWords id="lb-ghub-next-title" text="No problem in mind yet?" />
            <p>Pick an area and read what it involves, who it affects and where a student can start safely. Then come back to stop 1.</p>
          </div>
          <ul className="lb-ghub-next__areas">
            {problemCategories.map((problem) => (
              <li key={problem.slug}>
                <Link to={`/problems/${problem.slug}`}>
                  <span>{problem.title}</span>
                  <ArrowUpRight weight="bold" aria-hidden="true" />
                </Link>
              </li>
            ))}
          </ul>
          <p className="lb-ghub-next__mail">
            Stuck on a step? Write to <a href="mailto:founder@projectgrid.org">founder@projectgrid.org</a>
          </p>
        </section>
      </main>
    </PageFrame>
  );
}
